import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Button,
  Box,
  Chip,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { AccountCircle, Phone, Email } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import * as API from "../../api";

const useStyles = makeStyles(theme => ({
  statusCard: {
    height: "100%",
    borderRadius: theme.spacing(1),
  },
  statusTitle: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  verifiedChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: "bold",
    marginTop: -8,
    marginLeft: -2,
  },
  notVerifiedChip: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    fontWeight: "bold",
    marginTop: -8,
    marginLeft: -2,
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  actionButton: {
    marginTop: theme.spacing(1),
    textTransform: "none",
  },
}));

/**
 * A component that displays the verification status of the user's phone and email.
 *
 * The component shows whether the user's phone number and email address have been verified.
 * It provides options to resend the OTP for phone verification and resend the verification
 * email if they have not been verified. Upon successful email verification, a snackbar
 * message is displayed to inform the user.
 *
 * @param {object} props - component props
 * @param {object} props.currentUser - The current user's data, including verification status.
 * @param {boolean} props.otherProfile - Indicates if displaying another user's profile;
 *
 */
function DetailsVerificationTile({ currentUser, otherProfile }) {
  const classes = useStyles();
  const [loadingSendOTP, setLoadingSendOTP] = useState(false);
  const [loadingSendEmail, setLoadingSendEmail] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");

  const { phone_verified: phoneVerified, email_verified: emailVerified } =
    currentUser;

  const history = useHistory();

  const closeSnackBar = () => {
    setIsSnackbarOpen(false);
  };

  const handleSendOtp = async () => {
    setLoadingSendOTP(true);
    try {
      const updateAndSendVerification = await API.updateEmailAndPhone({
        authId: currentUser.auth_id,
        email: currentUser.email,
        phone: currentUser.phone,
        emailChanged: false,
        phoneChanged: true,
      });

      if (updateAndSendVerification.data.code === 200) {
        setLoadingSendOTP(false);
        history.push("/verify-phone");
      } else {
        setLoadingSendOTP(false);
      }
    } catch (error) {
      setLoadingSendOTP(false);
      console.error("Error sending OTP:", error);
    }
  };

  const handleSendVerificationEmail = async () => {
    setLoadingSendEmail(true);
    try {
      const updateAndSendVerification = await API.updateEmailAndPhone({
        authId: currentUser.auth_id,
        email: currentUser.email,
        phone: currentUser.phone,
        emailChanged: true,
        phoneChanged: false,
      });

      if (updateAndSendVerification.data.code === 200) {
        setLoadingSendEmail(false);
        setSnackBarMessage(
          "Email verification link has been sent to your email address."
        );
        setIsSnackbarOpen(true);
      } else {
        setLoadingSendEmail(false);
      }
    } catch (error) {
      setLoadingSendEmail(false);
      console.error("Error sending verification email:", error);
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Card
        className={classes.statusCard}
        style={{
          border: `2px solid ${
            phoneVerified && emailVerified ? "#2e7d32" : "#ed3413"
          }`,
        }}
      >
        <CardContent>
          <Typography variant="h6" className={classes.statusTitle}>
            <AccountCircle className={classes.statusIcon} />
            Verification Status
          </Typography>

          <Typography
            variant="subtitle2"
            color="textSecondary"
            style={{ paddingLeft: "8px" }}
            gutterBottom
          >
            Your contact information status
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            style={{
              width: "100%",
              gap: 8, // spacing between the two columns
              justifyContent: "center",
              alignContent: "center",
            }}
          >
            {" "}
            <Box flex={1} maxWidth={300}>
              {" "}
              <Box display="flex" alignItems="center" mb={1}>
                {" "}
                <Phone
                  color="primary"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
                <Typography variant="h6" className={classes.sectionTitle}>
                  Phone
                </Typography>
              </Box>
              {phoneVerified ? (
                <Box>
                  <Chip
                    label="Verified"
                    className={classes.verifiedChip}
                    size="small"
                  />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {otherProfile ? `${currentUser.first_name}'s` : "Your"}{" "}
                    phone number has been verified
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Chip
                    label="Not Verified"
                    className={classes.notVerifiedChip}
                    size="small"
                  />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {otherProfile ? `${currentUser.first_name}'s` : "Your"}{" "}
                    phone number has not been verified
                  </Typography>
                  {!otherProfile && (
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={loadingSendOTP}
                      onClick={handleSendOtp}
                      className={classes.actionButton}
                      size="small"
                    >
                      {loadingSendOTP ? "Loading..." : "Resend OTP"}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
            <Box flex={1} maxWidth={300}>
              <Box display="flex" alignItems="center" mb={1}>
                <Email
                  color="primary"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
                <Typography variant="h6" className={classes.sectionTitle}>
                  Email
                </Typography>
              </Box>

              {emailVerified ? (
                <Box>
                  <Chip
                    label="Verified"
                    className={classes.verifiedChip}
                    size="small"
                  />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {otherProfile ? `${currentUser.first_name}'s` : "Your"}{" "}
                    email address has been verified
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Chip
                    label="Not Verified"
                    className={classes.notVerifiedChip}
                    size="small"
                  />
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {otherProfile ? `${currentUser.first_name}'s` : "Your"}{" "}
                    email address has not been verified
                  </Typography>
                  {!otherProfile && (
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={loadingSendEmail}
                      onClick={handleSendVerificationEmail}
                      className={classes.actionButton}
                      size="small"
                    >
                      {loadingSendEmail
                        ? "Loading..."
                        : "Resend Verification Email"}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={4000}
        onClose={closeSnackBar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity="success" onClose={closeSnackBar}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default DetailsVerificationTile;
